export type GazetteerTuple = [
  id: string,
  name: string,
  aliases: string[],
  latitude: number,
  longitude: number,
  kind: string,
  rank: number,
  packIds: string[],
  elevationM?: number | null,
  elevationSource?: string | null,
  elevationStatus?: string | null,
];

export type GazetteerMatch = {
  tuple: GazetteerTuple;
  mapPackId: string | null;
  score: number;
};

export function normalizePlaceQuery(value: string) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function fieldScore(value: string, query: string, base: number) {
  if (!value) return Number.POSITIVE_INFINITY;
  if (value === query) return base;
  if (value.startsWith(query)) return base + 8;
  if (value.split(" ").some((word) => word.startsWith(query))) return base + 14;
  if (value.includes(query)) return base + 24;
  return Number.POSITIVE_INFINITY;
}

function levenshteinWithin(left: string, right: string, maxDistance: number) {
  if (Math.abs(left.length - right.length) > maxDistance) return maxDistance + 1;
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let row = 1; row <= left.length; row += 1) {
    let rowMinimum = row;
    const current = [row];
    for (let column = 1; column <= right.length; column += 1) {
      const substitution = previous[column - 1] + (left[row - 1] === right[column - 1] ? 0 : 1);
      const value = Math.min(previous[column] + 1, current[column - 1] + 1, substitution);
      current[column] = value;
      rowMinimum = Math.min(rowMinimum, value);
    }
    if (rowMinimum > maxDistance) return maxDistance + 1;
    for (let index = 0; index < current.length; index += 1) previous[index] = current[index];
  }
  return previous[right.length];
}

function fuzzyScore(value: string, query: string, base: number) {
  if (query.length < 4 || value.length < 3) return Number.POSITIVE_INFINITY;
  const candidates = [value, ...value.split(" ")]
    .filter((candidate) => Math.abs(candidate.length - query.length) <= 2);
  let best = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const maxDistance = query.length >= 8 ? 2 : 1;
    const distance = levenshteinWithin(candidate, query, maxDistance);
    if (distance <= maxDistance) best = Math.min(best, base + 44 + distance * 5);
  }
  return best;
}

function matchScore(tuple: GazetteerTuple, normalizedQuery: string) {
  const [, name, aliases, , , kind, rank] = tuple;
  const normalizedName = normalizePlaceQuery(name);
  const normalizedAliases = aliases.map(normalizePlaceQuery).filter(Boolean);
  const normalizedKind = normalizePlaceQuery(kind);

  // Primero resolvemos coincidencias exactas/prefijo/contenido. Esto evita
  // ejecutar distancia de edición sobre miles de nombres cuando no hace falta.
  const direct = Math.min(
    fieldScore(normalizedName, normalizedQuery, 0),
    ...normalizedAliases.map((alias) => fieldScore(alias, normalizedQuery, 2)),
    fieldScore(`${normalizedKind} ${normalizedName}`, normalizedQuery, 9),
  );
  let textual = direct;
  if (!Number.isFinite(textual) && normalizedQuery.length >= 4) {
    const fuzzyCandidates = [normalizedName, ...normalizedAliases]
      .filter((candidate) => candidate[0] === normalizedQuery[0]);
    textual = Math.min(
      ...fuzzyCandidates.map((candidate, index) => fuzzyScore(candidate, normalizedQuery, index === 0 ? 0 : 2)),
      Number.POSITIVE_INFINITY,
    );
  }
  if (!Number.isFinite(textual)) return Number.POSITIVE_INFINITY;
  // La relevancia del catálogo solo desempata; nunca hace que una coincidencia
  // parcial gane a un nombre exacto.
  return textual - Math.min(9, Math.max(0, Number(rank) || 0) / 150);
}

function selectMapPackId(packIds: string[], allowedPackIds: ReadonlySet<string> | null, preferredPackId?: string | null) {
  const candidates = allowedPackIds
    ? packIds.filter((id) => allowedPackIds.has(id))
    : packIds.filter((id) => id !== "ecuador");
  if (!candidates.length) return null;
  if (preferredPackId && candidates.includes(preferredPackId)) return preferredPackId;
  return candidates[0] ?? null;
}

export function searchGazetteerTuples(
  tuples: GazetteerTuple[],
  query: string,
  options: {
    allowedPackIds?: ReadonlySet<string> | null;
    preferredPackId?: string | null;
    limit?: number;
  } = {},
): GazetteerMatch[] {
  const normalizedQuery = normalizePlaceQuery(query);
  if (normalizedQuery.length < 2) return [];
  const allowedPackIds = options.allowedPackIds === undefined ? null : options.allowedPackIds;
  const limit = Math.max(1, Math.min(20, options.limit ?? 8));

  return tuples
    .flatMap((tuple) => {
      if (!Array.isArray(tuple) || tuple.length < 8) return [];
      const mapPackId = selectMapPackId(tuple[7] ?? [], allowedPackIds, options.preferredPackId);
      if (allowedPackIds && !mapPackId) return [];
      const score = matchScore(tuple, normalizedQuery);
      return Number.isFinite(score) ? [{ tuple, mapPackId, score }] : [];
    })
    .sort((left, right) => left.score - right.score
      || Number(right.tuple[6] || 0) - Number(left.tuple[6] || 0)
      || String(left.tuple[1]).localeCompare(String(right.tuple[1]), "es"))
    .slice(0, limit);
}
